// Audience picker for the broadcast composer. Three flavors selected by
// `kind`; each fetches a distinct admin picker endpoint and renders pills
// in the matching tone (emerald/orange/neutral). The composer owns the
// id list — this component is a controlled multi-select around
// TypeaheadMultiSelect.

import { useCallback } from 'react';
import { Avatar, Field, Row, Stack } from '@leanin/ui';
import { Body, Caption } from '@leanin/ui/server';

import { getApiClient } from '../../lib/api-client';
import { TypeaheadMultiSelect, type TypeaheadItem } from './TypeaheadMultiSelect';

export type AudiencePickerKind = 'circles' | 'networks' | 'members';

interface CircleItem extends TypeaheadItem {
  readonly id: string;
  readonly name: string;
  readonly kind: 'circle';
}

interface NetworkItem extends TypeaheadItem {
  readonly id: string;
  readonly name: string;
  readonly kind: 'network';
}

interface MemberItem extends TypeaheadItem {
  readonly id: string;
  readonly displayName: string;
  readonly email: string;
}

interface BaseProps {
  readonly selectedIds: readonly string[];
}

interface CirclesProps extends BaseProps {
  readonly kind: 'circles';
  readonly selectedItems: readonly CircleItem[];
  readonly onChange: (ids: readonly string[], items: readonly CircleItem[]) => void;
}

interface NetworksProps extends BaseProps {
  readonly kind: 'networks';
  readonly selectedItems: readonly NetworkItem[];
  readonly onChange: (ids: readonly string[], items: readonly NetworkItem[]) => void;
}

interface MembersProps extends BaseProps {
  readonly kind: 'members';
  readonly selectedItems: readonly MemberItem[];
  readonly onChange: (ids: readonly string[], items: readonly MemberItem[]) => void;
}

export type BroadcastAudiencePickerProps = CirclesProps | NetworksProps | MembersProps;

const PAGE_SIZE = 20;

export function BroadcastAudiencePicker(props: BroadcastAudiencePickerProps) {
  if (props.kind === 'circles') return <CirclesPicker {...props} />;
  if (props.kind === 'networks') return <NetworksPicker {...props} />;
  return <MembersPicker {...props} />;
}

function CirclesPicker({ selectedIds, selectedItems, onChange }: CirclesProps) {
  const fetcher = useCallback(async (q: string): Promise<readonly CircleItem[]> => {
    const client = getApiClient();
    const { data } = await client.GET('/api/v1/admin/circles', {
      params: { query: { q: q.trim() === '' ? undefined : q.trim(), pageSize: PAGE_SIZE } },
    });
    return data?.items ?? [];
  }, []);

  return (
    <Field
      label="Circles"
      description="Type to search circles. Selected circles appear as removable chips."
      required
    >
      <TypeaheadMultiSelect<CircleItem>
        placeholder="Search circles…"
        fetcher={fetcher}
        selectedIds={selectedIds}
        selectedItems={selectedItems}
        onChange={onChange}
        renderItem={(item) => <Body size="sm">{item.name}</Body>}
        renderSelectedLabel={(item) => item.name}
        pillTone="emerald"
        emptyState="No circles match"
      />
    </Field>
  );
}

function NetworksPicker({ selectedIds, selectedItems, onChange }: NetworksProps) {
  const fetcher = useCallback(async (q: string): Promise<readonly NetworkItem[]> => {
    const client = getApiClient();
    const { data } = await client.GET('/api/v1/admin/networks', {
      params: { query: { q: q.trim() === '' ? undefined : q.trim(), pageSize: PAGE_SIZE } },
    });
    return data?.items ?? [];
  }, []);

  return (
    <Field
      label="Networks"
      description="Type to search networks. Selected networks appear as removable chips."
      required
    >
      <TypeaheadMultiSelect<NetworkItem>
        placeholder="Search networks…"
        fetcher={fetcher}
        selectedIds={selectedIds}
        selectedItems={selectedItems}
        onChange={onChange}
        renderItem={(item) => <Body size="sm">{item.name}</Body>}
        renderSelectedLabel={(item) => item.name}
        pillTone="orange"
        emptyState="No networks match"
      />
    </Field>
  );
}

function MembersPicker({ selectedIds, selectedItems, onChange }: MembersProps) {
  const fetcher = useCallback(async (q: string): Promise<readonly MemberItem[]> => {
    const client = getApiClient();
    const trimmed = q.trim();
    if (trimmed.length === 0) return [];
    const { data } = await client.GET('/api/v1/admin/members/search', {
      params: { query: { q: trimmed, pageSize: PAGE_SIZE } },
    });
    return data?.items ?? [];
  }, []);

  return (
    <Field
      label="Members"
      description="Type a name or email to find members. Selected members appear as removable chips."
      required
    >
      <TypeaheadMultiSelect<MemberItem>
        placeholder="Search members by name or email…"
        fetcher={fetcher}
        selectedIds={selectedIds}
        selectedItems={selectedItems}
        onChange={onChange}
        renderItem={(item) => (
          <Row gap="sm" align="center">
            <Avatar name={item.displayName} size="sm" decorative />
            <Stack gap="xs">
              <Body size="sm">{item.displayName}</Body>
              <Caption>{item.email}</Caption>
            </Stack>
          </Row>
        )}
        renderSelectedLabel={(item) => item.displayName}
        pillTone="neutral"
        emptyState="Start typing to search"
      />
    </Field>
  );
}
